import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Shield,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { PermissionGrid } from './PermissionGrid';

interface TeamMember {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  status: 'Active' | 'Inactive' | 'Pending';
  permissions: string[];
  role: string;
}

interface EditPermissionsProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  onSuccess?: () => void;
  updateMemberPermissions: (memberId: number, permissions: any[]) => Promise<any>;
}

import { TeamAPI } from '../api/team';

interface ModulePermission {
  modulecode: string;
  modulename: string;
  actions: { [key: string]: boolean };
  availableActions?: { [key: string]: boolean };
}

interface ModuleData {
  id: number;
  modulecode: string;
  modulename: string;
  actions: string[];
  category?: string;
}


export function EditPermissions({
  isOpen,
  onClose,
  member,
  onSuccess,
  updateMemberPermissions
}: EditPermissionsProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modulePermissions, setModulePermissions] = useState<{ [modulecode: string]: { [action: string]: boolean } }>({});
  const [availableModules, setAvailableModules] = useState<ModuleData[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setIsLoadingModules(true);
        const response = await TeamAPI.getModules();
        if (response.code === '200' && response.data) {
          setAvailableModules(response.data);
        }
      } catch (error) {
        setError('Failed to load available modules');
      } finally {
        setIsLoadingModules(false);
      }
    };

    fetchModules();
  }, []);

  useEffect(() => {
    const fetchMemberPermissions = async () => {
      if (!member) return;

      try {
        const response = await TeamAPI.getMemberPermissions(member.id);

        if (response.code === '200' && response.data) {
          const initialModulePerms: { [modulecode: string]: { [action: string]: boolean } } = {};

          response.data.forEach((perm: any) => {
            if (perm.modulecode && perm.actions) {
              initialModulePerms[perm.modulecode] = perm.actions;
            }
          });

          setModulePermissions(initialModulePerms);
        } else {
          const legacyPermissions = member.permissions || [];
          const initialModulePerms: { [modulecode: string]: { [action: string]: boolean } } = {};

          legacyPermissions.forEach(perm => {
            const parts = perm.split('_');
            if (parts.length >= 2) {
              const action = parts[0];
              const modulecode = parts.slice(1).join('_');

              if (!initialModulePerms[modulecode]) {
                initialModulePerms[modulecode] = {};
              }
              initialModulePerms[modulecode][action] = true;
            }
          });

          setModulePermissions(initialModulePerms);
        }
      } catch (error) {
        setModulePermissions({});
      }
    };

    fetchMemberPermissions();
  }, [member]);

  const handleSavePermissions = async () => {
    if (!member) return;

    setIsProcessing(true);
    setError(null);

    try {
      const permissions = Object.entries(modulePermissions)
        .filter(([_, actions]) => Object.keys(actions).length > 0)
        .map(([modulecode, actions]) => ({
          modulecode,
          actions
        }));

      const response = await updateMemberPermissions(member.id, permissions);

      if (response.success) {
        setIsProcessing(false);
        setIsComplete(true);

        onClose();
        setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to update permissions');
      }
    } catch (error) {
      setIsProcessing(false);
      setError(error instanceof Error ? error.message : 'Failed to update permissions. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const toggleModuleAction = (modulecode: string, action: string) => {
    setModulePermissions(prev => {
      const newPerms = { ...prev };
      if (!newPerms[modulecode]) {
        newPerms[modulecode] = {};
      }

      if (newPerms[modulecode][action]) {
        delete newPerms[modulecode][action];
        if (Object.keys(newPerms[modulecode]).length === 0) {
          delete newPerms[modulecode];
        }
      } else {
        newPerms[modulecode][action] = true;
      }

      return newPerms;
    });
  };

  const toggleAllModuleActions = (modulecode: string, actions: string[]) => {
    setModulePermissions(prev => {
      const newPerms = { ...prev };
      const currentModule = newPerms[modulecode] || {};
      const allEnabled = actions.every(action => currentModule[action]);

      if (allEnabled) {
        delete newPerms[modulecode];
      } else {
        newPerms[modulecode] = {};
        actions.forEach(action => {
          newPerms[modulecode][action] = true;
        });
      }

      return newPerms;
    });
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Permissions Updated Successfully!
          </h3>
          <p className="text-muted-foreground">
            {member?.firstname} {member?.lastname}'s permissions have been updated.
          </p>
        </div>
      );
    }

    if (isProcessing) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Updating Permissions...
          </h3>
          <p className="text-muted-foreground">
            Saving changes for {member?.firstname} {member?.lastname}...
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <PermissionGrid
            availableModules={availableModules}
            modulePermissions={modulePermissions}
            onToggleAction={toggleModuleAction}
            onToggleAllActions={toggleAllModuleActions}
            isLoading={isLoadingModules}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Permission Summary</h4>
            <p className="text-sm text-blue-800">
              {Object.keys(modulePermissions).length === 0 ? (
                'No permissions assigned yet.'
              ) : (
                <>
                  Member will have access to <strong>{Object.keys(modulePermissions).length}</strong> module{Object.keys(modulePermissions).length !== 1 ? 's' : ''} with a total of{' '}
                  <strong>
                    {Object.values(modulePermissions).reduce((sum, actions) => sum + Object.keys(actions).length, 0)}
                  </strong> action{Object.values(modulePermissions).reduce((sum, actions) => sum + Object.keys(actions).length, 0) !== 1 ? 's' : ''}.
                </>
              )}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <span>Edit Permissions</span>
          </DialogTitle>
          <DialogDescription>
          Select specific actions for each module that this member can perform.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>

        {!isProcessing && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePermissions}
              className="bg-primary hover:bg-primary/90"
            >
              Save Changes
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
